import React, { Suspense, useEffect, useMemo, useRef } from 'react';
import { useGlobal } from 'reactn';
import { FolderNav, Theme, FeedsNewMessageModal, Badge, Loader, FeedsMessageView } from '@orijinworks/frontend-commons';
import { Box, CircularProgress, useTheme } from '@material-ui/core';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useFeedsManager, useAttorneyMessagingHook, useAdminLearnerMessagingHook } from './hooks';
import { useDocumentTitleHook } from '../../shared/hooks';
import FeedsErrorBoundary from './components/FeedsErrorBoundary';
import { useFeedConfigureHook } from './useFeedConfigureHook';
import { FeedMembersAndReceiptsInfoModal } from '../../shared/components';
import { AuditLogButton, FeedsApplicationStyles } from './Feeds.style';
import HistoryIcon from '@mui/icons-material/History';
import { Link, useLocation } from 'react-router-dom';
import AttorneyModal from './components/AttorneyModal';
import AdminLearnerModal from './components/AdminLearnerModal';
import withRolePermission from './withRolePermission';
import AllFeedsWithAttachmentPreview from './components/AllFeeds/AllFeedsWithAttachmentPreview';
import PermissionControl, { isPermitted } from '../../../components/permission-control/permission-control';
import { FEED_AUDIT, FEED_ATTACHMENT_CREATE } from './FeedPermission';
import { UnleashService } from '../../services';
import useUnleashHook from '../../core/unleash/useUnleashHook';
import { FORM_ATTACH } from '../../Form/constants/form.permission';
import Forms from '../../Form/Forms';
import AttachFormModal from './components/AttachFormModal';
import InactiveFormAttachmentModal from './components/InactiveFormAttachmentModal/InactiveFormAttachmentModal';

// Lazy load components for better performance
const Announcements = React.lazy(() => import('./components/Announcements/Announcements'));
const Messages = React.lazy(() => import('./components/Messages/Messages'));

const HASH_TO_TAB = {
  form: 'Forms',
  forms: 'Forms',
  messages: 'Messages',
  announcement: 'Announcements',
  announcements: 'Announcements',
};

const useFeedsHashTabSync = (location, visibleTabValues, onTabChangeRef, activeTabRef) => {
  useEffect(() => {
    const applyHashToTab = () => {
      const fromWindow =
        typeof window !== 'undefined' ? window.location.hash ?? '' : '';
      const rawHash = fromWindow || location.hash || '';
      const fragment = rawHash.replace(/^#/, '').toLowerCase();
      const targetTab = HASH_TO_TAB[fragment];
      if (!targetTab || !visibleTabValues.includes(targetTab)) {
        return;
      }
      if (activeTabRef.current === targetTab) {
        return;
      }
      onTabChangeRef.current(null, targetTab);
    };
    applyHashToTab();
    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', applyHashToTab);
      return () => window.removeEventListener('hashchange', applyHashToTab);
    }
    return undefined;
  }, [location.hash, visibleTabValues, onTabChangeRef, activeTabRef]);
};

const buildTabsFromConfig = (tabsConfig) =>
  tabsConfig
    .filter((tabConfig) => !tabConfig.hide)
    .map((tabConfig) => {
      const allFeedsTabLabel = tabConfig.key === 'all' ? 'all-feeds-tab-label' : undefined;
      const label = tabConfig.hasUnreadCount
        ? createLabelWithCount(tabConfig.value, tabConfig.unreadCount, allFeedsTabLabel)
        : tabConfig.value;
      return {
        label,
        value: tabConfig.value,
        Icon: tabConfig.Icon,
      };
    });

const buildDmReadReceiptsModalEl = (feedsManager, theme) => {
  if (!feedsManager?.dm?.readReceiptsModal?.open) {
    return null;
  }
  return (
    <FeedMembersAndReceiptsInfoModal
      open={feedsManager.dm.readReceiptsModal.open}
      onClose={feedsManager.dm.readReceiptsModal.onClose}
      heading={feedsManager.dm.readReceiptsModal.heading}
      badgeLabel={feedsManager.dm.readReceiptsModal.badgeLabel}
      members={feedsManager.dm.readReceiptsModal.members}
      headingIcon={
        <DoneAllIcon
          fontSize="small"
          sx={{ color: theme?.palette?.colors?.blueMain }}
        />
      }
      showFooter={false}
      styles={{
        mainWrapperStyles: {
          maxHeight: '450px',
          overflowY: 'auto',
          height: 'auto !important',
          borderBottom: 'none !important',
        },
      }}
    />
  );
};

const createLabelWithCount = (value, count, testId) => (
  <span {...(testId && { 'data-testid': testId })}>
    {value}
    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
    {count > 0 && (
      <Badge
        themeColor="red"
        variant="standard"
        badgeContent={count > 99 ? '99+' : count}
        style={{ padding: 0, fontSize: 12 }}
      />
    )}
  </span>
);

/**
 * Feeds Page - Optimized with Unified Hook Architecture
 *
 * Uses useFeedsManager to prevent unnecessary re-renders:
 * - All Feeds: Feed listing, pagination, socket events for new messages
 * - Direct Messages: Conversation management, messaging, thread handling
 * - Clean data flow and business logic separation
 * - Hooks access user data directly from localStorage
 * - No stale closure issues or reference problems
 * - Implements React.memo for performance optimization
 * - Uses lazy loading for better initial load performance
 *
 * @returns {JSX.Element}
 */
const Feeds = React.memo(() => {
  // Set document title
  useDocumentTitleHook('feed');
  const [global] = useGlobal();
  const feedsManager = useFeedsManager();
  const theme = useTheme();
  const {
    showNewMessageModal,
    modalContext,
    editSettings,
    tabsConfig,
    handleNewMessage,
    handleCloseModal,
    handleOpenEditModal } = useFeedConfigureHook({
      unreadCount: feedsManager?.totalUnreadCount,
      unreadAnnouncementsCount: feedsManager?.unreadCounts?.unreadAnnouncementsCount,
      unreadMessagesCount: feedsManager?.unreadCounts?.unreadMessagesCount,
      unreadChannelsCount: feedsManager?.unreadCounts?.unreadChannelsCount,
      resetAnnouncementAttachments: feedsManager.manageFeedProps.resetAttachments,
    });

  const location = useLocation();
  const visibleTabValues = useMemo(
    () => tabsConfig.filter((tabConfig) => !tabConfig.hide).map((tabConfig) => tabConfig.value),
    [tabsConfig],
  );

  const onTabChangeRef = useRef(feedsManager.onTabChangeHandler);
  const activeTabRef = useRef(feedsManager.tabNavigation.activeTab);
  onTabChangeRef.current = feedsManager.onTabChangeHandler;
  activeTabRef.current = feedsManager.tabNavigation.activeTab;

  useFeedsHashTabSync(location, visibleTabValues, onTabChangeRef, activeTabRef);

  // Attorney modal state and handlers
  const {
    canShowAttorneyLink,
    showAttorneyModal,
    handleOpenAttorneyModal,
    handleCloseAttorneyModal,
  } = useAttorneyMessagingHook(handleCloseModal);

  // Admin-learner modal state and handlers
  const {
    canShowAdminLearnerLink,
    showAdminLearnerModal,
    handleOpenAdminLearnerModal,
    handleCloseAdminLearnerModal,
  } = useAdminLearnerMessagingHook(handleCloseModal);

  const isAttachmentUploadFlagEnabled = useUnleashHook(UnleashService.FLAGS.FEED_ATTACHMENTS_UPLOAD).isFlagEnabled();

  // File upload in FeedsNewMessageModal (conversation + announcement): same gate as DM composer
  const canUploadFile =
    isAttachmentUploadFlagEnabled &&
    isPermitted(
      global.cbacConfigFlag,
      global.permissions,
      FEED_ATTACHMENT_CREATE,
      global.permissionIds
    );

  // Attach-form action in the composer toolbar gated by form_attach.
  const canAttachForm = isPermitted(
    global.cbacConfigFlag,
    global.permissions,
    FORM_ATTACH,
    global.permissionIds
  );

  const tabs = buildTabsFromConfig(tabsConfig);
  const dmReadReceiptsModalEl = buildDmReadReceiptsModalEl(feedsManager, theme);

  if (showNewMessageModal) {
    const { fileInputRef, handleFileChange } = feedsManager.manageFeedProps;
    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          accept="*/*"
          aria-hidden
        />
        <FeedsNewMessageModal
          hasLearner={feedsManager.dm.feedDetails?.hasLearner}
          toolbarOptions={{
            fileUpload: canUploadFile,
            attachForm: canAttachForm && Boolean(feedsManager.manageFeedProps.onAttachFormClick),
          }}
          filesList={
            modalContext === 'announcement'
              ? feedsManager.manageFeedProps.announcementEditorFilesList
              : feedsManager.manageFeedProps.attachmentFiles
          }
          onFileUploadClick={feedsManager.manageFeedProps.onFileUploadClick}
          onAttachFormClick={feedsManager.manageFeedProps.onAttachFormClick}
          snackbarError={feedsManager.manageFeedProps.snackbarError}
          onSnackbarClose={feedsManager.manageFeedProps.onSnackbarClose}
          manageFeedPermissions={feedsManager.manageFeedPermissions}
          modalContext={modalContext}
          onClose={handleCloseModal}
          memberOptions={feedsManager.manageFeedProps.searchedMembers}
          onMemberOptionsChange={feedsManager.manageFeedProps.onSearchChangeHandler}
          initialConversationSettings={{
            learnerCanSendMessages: true,
            limitLearnerMessages: true,
            numberOfMessages: feedsManager.manageFeedProps.DEFAULT_NUMBER_OF_MESSAGES_ALLOWED,
            shutDownConversation: false,
          }}
          onCreateConversation={feedsManager.manageFeedProps.onCreateHandler}
          onCreateAnnouncement={feedsManager.manageFeedProps.onCreateAnnouncement}
          maximumMembersAllowed={modalContext === 'message' ? 1 : undefined}
          showSettings={true}
          isMemberListLoading={feedsManager.manageFeedProps.isMemberSearchLoading}
          isCreateLoading={feedsManager.manageFeedProps.isManageFeedLoading}
          {...(canShowAttorneyLink && { onOpenAttorneyModal: handleOpenAttorneyModal })}
          {...(canShowAdminLearnerLink && { onOpenAdminLearnerModal: handleOpenAdminLearnerModal })}
          {...(modalContext === 'edit' && {
            onSaveChanges: feedsManager.manageFeedProps.onSaveChanges,
            editSettings: editSettings,
          })}
        />
        <AttachFormModal
          open={feedsManager.attachFormModal?.open}
          onClose={feedsManager.attachFormModal?.onClose}
          onFormConfirm={feedsManager.attachFormModal?.onFormConfirm}
          attachedForms={feedsManager.attachFormModal?.attachedForms}
        />
      </>
    );
  }

  if (showAttorneyModal) {
    return (
      <AttorneyModal
        onClose={handleCloseAttorneyModal}
        onCreateConversation={feedsManager.manageFeedProps.onCreateHandler}
        maximumMembersAllowed={10}
        isCreateLoading={feedsManager.manageFeedProps.isManageFeedLoading}
        initialConversationSettings={{
          learnerCanSendMessages: true,
          limitLearnerMessages: true,
          numberOfMessages: feedsManager.manageFeedProps.DEFAULT_NUMBER_OF_MESSAGES_ALLOWED,
          shutDownConversation: false,
        }}
      />
    );
  }

  if (showAdminLearnerModal) {
    return (
      <AdminLearnerModal
        onClose={handleCloseAdminLearnerModal}
        onCreateConversation={feedsManager.manageFeedProps.onCreateHandler}
        isCreateLoading={feedsManager.manageFeedProps.isManageFeedLoading}
        initialConversationSettings={{
          learnerCanSendMessages: true,
          limitLearnerMessages: true,
          numberOfMessages: feedsManager.manageFeedProps.DEFAULT_NUMBER_OF_MESSAGES_ALLOWED,
          shutDownConversation: false,
        }}
      />
    );
  }


  if (feedsManager.dm.messageView && typeof feedsManager.dm.messageView === 'object') {
    return (

      <Theme>
        <Loader isLoading={global.isLoading} />
        <Box sx={{
          height: 'calc(100vh - 120px)',
          backgroundColor: 'white',
          padding: '15px 0'
        }}>
          <input
            ref={feedsManager.dm.fileInputRef}
            type="file"
            onChange={feedsManager.dm.handleFileChange}
            style={{ display: 'none' }}
            accept="*/*"
            aria-hidden
          />
          <FeedsMessageView
            {...feedsManager.dm}
            {...feedsManager.dm.messageView}
            ref={feedsManager.messageViewRef}
            onEditSettingsHandler={handleOpenEditModal}
            onFeedSettingEditHandler={feedsManager.manageFeedProps.onFeedSettingEditHandler}
          />
          <AttachFormModal
            open={feedsManager.attachFormModal?.open}
            onClose={feedsManager.attachFormModal?.onClose}
            onFormConfirm={feedsManager.attachFormModal?.onFormConfirm}
            attachedForms={feedsManager.attachFormModal?.attachedForms}
          />
          <InactiveFormAttachmentModal
            open={Boolean(feedsManager.dm?.isInactiveFormModalOpen)}
            onClose={feedsManager.dm?.onCloseInactiveFormModal}
          />
        </Box>
      </Theme>
    );
  }
  return (
    <Theme>
      <Loader isLoading={global.isLoading} />
      <FeedsErrorBoundary>
        <Box
          role="main"
          aria-label="Feeds application"
          position="relative"
          sx={FeedsApplicationStyles}
        >
          <Box>
            <PermissionControl permissionName={FEED_AUDIT}>
              <AuditLogButton
                variant="outlined" color="primary"
                LinkComponent={Link}
                to="/feeds/audit"
              >
                <HistoryIcon />
                Audit Log
              </AuditLogButton>
            </PermissionControl>
          </Box>
          <FolderNav
            scrollButtons={false}
            tabFontColor={'var(--blue-main)'}
            tabs={tabs}
            defaultActiveTab={feedsManager.tabNavigation.activeTab}
            onTabChange={feedsManager.onTabChangeHandler}
            role="tablist"
            aria-label="Feed navigation tabs"
          >
            {feedsManager.tabNavigation.activeTab === 'All' && (
              <div role="tabpanel" aria-labelledby="all-tab" id="all-panel">
                <AllFeedsWithAttachmentPreview
                  {...feedsManager.allFeeds}
                />
              </div>
            )}
            {feedsManager.tabNavigation.activeTab === 'Messages' && (
              <Suspense fallback={<Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>}>
                <Messages
                  {...feedsManager.dm}
                  onNewMessageHandler={() => handleNewMessage('message')}
                  onFeedSettingEditHandler={feedsManager.manageFeedProps.onFeedSettingEditHandler}
                  onEditSettingsHandler={handleOpenEditModal}
                  messageViewRef={feedsManager.messageViewRef}
                />
              </Suspense>
            )}
            {feedsManager.tabNavigation.activeTab === 'Announcements' && (
              <div role="tabpanel" aria-labelledby="announcements-tab" id="announcements-panel">
                <Suspense fallback={<Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>}>
                  <Announcements
                    {...feedsManager.announcements}
                    onNewAnnouncementHandler={() => handleNewMessage('announcement')}
                    onAnnouncementDeleteHandler={feedsManager.announcements.onAnnouncementDeleteHandler}
                  />
                </Suspense>
              </div>
            )}
            {feedsManager.tabNavigation.activeTab === 'Forms' && (
              <div role="tabpanel" aria-labelledby="form-tab" id="form-panel" style={{ height: '93%' }}>
                <Suspense fallback={<Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>}>
                  <Forms
                  />
                </Suspense>
              </div>
            )}
          </FolderNav>
        </Box>
        {/* Read Receipts Modal - rendered at top level to avoid prop drilling */}
        {dmReadReceiptsModalEl}
        <AttachFormModal
          open={feedsManager.attachFormModal?.open}
          onClose={feedsManager.attachFormModal?.onClose}
          onFormConfirm={feedsManager.attachFormModal?.onFormConfirm}
          attachedForms={feedsManager.attachFormModal?.attachedForms}
        />
        <InactiveFormAttachmentModal
          open={Boolean(feedsManager.announcements?.isInactiveFormModalOpen)}
          onClose={feedsManager.announcements?.onCloseInactiveFormModal}
        />
      </FeedsErrorBoundary>
    </Theme>
  );
});

export default withRolePermission(Feeds);
